/**
 * Exocortex - App Principal
 *
 * Componente raíz de la aplicación
 */

import { useEffect, useRef } from 'react';
import { useProtocolStore, useUIStore, useHistoryStore, useChecklistStore } from '@/store';
import { MainLayout } from '@/ui/layouts';
import { Dashboard, EnergyCheckIn, FocusMode, QuickTaskFAB } from '@/features/dashboard';
import { useCurrentTimeBlock } from '@/core/hooks';
import { ToastContainer, Modal, OfflineIndicator, InstallPrompt, UpdatePrompt } from '@/ui/composed';
import { SettingsPanel } from '@/features/settings';
import { StatsView } from '@/features/analytics';
import { PackView } from '@/features/pack';
import { PomodoroTimer } from '@/features/timer';
import { AchievementsProvider, AchievementsView } from '@/features/achievements';

export function App() {
  const initialize = useProtocolStore((state) => state.initialize);
  const checkAndResetStale = useProtocolStore((state) => state.checkAndResetStale);
  const checkAndAutoReset = useProtocolStore((state) => state.checkAndAutoReset);
  const modal = useUIStore((state) => state.modal);
  const closeModal = useUIStore((state) => state.closeModal);
  const activeView = useUIStore((state) => state.activeView);
  const hardDayMode = useUIStore((state) => state.hardDayMode);
  const preferences = useUIStore((state) => state.preferences);
  const initializeHistory = useHistoryStore((state) => state.initialize);
  const initializeChecklists = useChecklistStore((state) => state.initialize);
  const currentTimeBlock = useCurrentTimeBlock();

  // Mostrar FAB en dashboard (cualquier frecuencia), sin modo día difícil
  const showQuickTaskFAB = activeView === 'dashboard' && !hardDayMode;

  // Ref para capturar el valor actual de autoResetTime sin recrear intervals
  const autoResetTimeRef = useRef(preferences.autoResetTime);
  useEffect(() => {
    autoResetTimeRef.current = preferences.autoResetTime;
  }, [preferences.autoResetTime]);

  const setActiveView = useUIStore((state) => state.setActiveView);

  // Leer vista desde URL query parameter (para PWA shortcuts)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const viewParam = params.get('view');
    const validViews = ['dashboard', 'stats', 'pack', 'timer', 'achievements'];

    if (viewParam && validViews.includes(viewParam)) {
      setActiveView(viewParam as 'dashboard' | 'stats' | 'pack' | 'timer' | 'achievements');
      // Limpiar el parámetro de la URL sin recargar
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [setActiveView]);

  // Inicializar datos al montar
  useEffect(() => {
    initialize();
    initializeHistory();
    initializeChecklists();
  }, [initialize, initializeHistory, initializeChecklists]);

  // Auto-reset diario a la hora configurada
  // Usa ref para evitar recrear el interval cuando cambia autoResetTime
  useEffect(() => {
    // Verificar inmediatamente al cargar
    checkAndAutoReset(autoResetTimeRef.current);

    // Verificar cada minuto (usa ref para obtener valor actual)
    const interval = setInterval(() => {
      checkAndAutoReset(autoResetTimeRef.current);
    }, 60000); // 1 minuto

    return () => clearInterval(interval);
  }, [checkAndAutoReset]);

  // Verificar y resetear protocolos vencidos cada minuto
  useEffect(() => {
    const interval = setInterval(() => {
      checkAndResetStale();
    }, 60000); // 1 minuto

    return () => clearInterval(interval);
  }, [checkAndResetStale]);

  // Aplicar clase 'compact' al body cuando está activado
  useEffect(() => {
    if (preferences.compactMode) {
      document.body.classList.add('compact');
    } else {
      document.body.classList.remove('compact');
    }
  }, [preferences.compactMode]);

  // Aplicar clase para reducir movimiento si está configurado
  useEffect(() => {
    if (preferences.reduceMotion || !preferences.animationsEnabled) {
      document.body.classList.add('reduce-motion');
    } else {
      document.body.classList.remove('reduce-motion');
    }
  }, [preferences.reduceMotion, preferences.animationsEnabled]);

  // Aplicar clase para colores suaves (sensibilidad visual)
  useEffect(() => {
    if (preferences.softColors) {
      document.body.classList.add('soft-colors');
    } else {
      document.body.classList.remove('soft-colors');
    }
  }, [preferences.softColors]);

  // Aplicar clase para fuente accesible
  useEffect(() => {
    document.body.classList.remove('font-dyslexic', 'font-mono-all');
    if (preferences.fontFamily === 'dyslexic') {
      document.body.classList.add('font-dyslexic');
    } else if (preferences.fontFamily === 'mono') {
      document.body.classList.add('font-mono-all');
    }
  }, [preferences.fontFamily]);

  // Aplicar clase para alto contraste
  useEffect(() => {
    if (preferences.highContrast) {
      document.body.classList.add('high-contrast');
    } else {
      document.body.classList.remove('high-contrast');
    }
  }, [preferences.highContrast]);

  return (
    <AchievementsProvider>
      {/* Indicador de estado offline */}
      <OfflineIndicator />

      <MainLayout>
        {activeView === 'dashboard' && <Dashboard />}
        {activeView === 'pack' && <PackView />}
        {activeView === 'timer' && <PomodoroTimer />}
        {activeView === 'stats' && <StatsView />}
        {activeView === 'achievements' && <AchievementsView />}
      </MainLayout>

      {/* Modal de configuración */}
      <Modal
        isOpen={modal.isOpen && modal.type === 'settings'}
        onClose={closeModal}
        title="Configuración"
        description="Personaliza tu experiencia en Exocortex"
        size="md"
      >
        <SettingsPanel />
      </Modal>

      <ToastContainer />

      {/* Check-in de energia diario */}
      <EnergyCheckIn />

      {/* Modo Focus */}
      <FocusMode />

      {/* Prompt de instalacion PWA */}
      <InstallPrompt />

      {/* Prompt de actualización PWA */}
      <UpdatePrompt />

      {/* FAB para tarea rápida - solo en vista diaria */}
      {showQuickTaskFAB && <QuickTaskFAB currentTimeBlock={currentTimeBlock} />}
    </AchievementsProvider>
  );
}
